import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Phone, Clock, MapPin, Map, Image, Link2, Users, AlertTriangle } from 'lucide-react';
import {
  Modal,
  Button,
  ImageUpload,
  TextInput,
  PhoneInput,
  DocumentInput,
  SearchableComboBox,
  RichTextInput,
  BusinessHoursInput,
  defaultWeekHours,
  weekHoursFrom,
  weekHoursToPayload,
  isWeekHoursComplete,
  ExternalLinksInput,
  externalLinksToRequest,
  externalLinksToPayload,
  validateExternalLinks,
  hasExternalLinkErrors,
  MerchantTeamInput,
  emptyMerchantEmployee,
  merchantTeamToPayload,
  validateMerchantTeam,
  hasMerchantTeamErrors,
  isBlankMerchantEmployee,
} from '@kardume/ui';
import type { DayHours, WeekHours, MerchantEmployeeFormValue } from '@kardume/ui';
import type {
  ComboItem,
  MerchantDto,
  MerchantRequestDto,
  MerchantStatus,
  DayOfWeek,
  ExternalLinkPlatformOptionDto,
  ExternalLinkRequestDto,
  ResponsibleDto,
} from '@kardume/types';
import { MERCHANT_ROLE_LABELS, MERCHANT_STATUS_LABELS } from '@kardume/types';
import { useNotification } from '@kardume/hooks';
import { backofficeApi, UF_OPTIONS, lookupCep, maskCep, onlyCepDigits } from '@kardume/services';
import { uploadMerchantImage } from '../utils/imageUpload';

/** Mesmo formato que a validação da equipe usa, para as duas telas concordarem. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Coordenada digitada, como número - ou `undefined` (card 150).
 *
 * Campo em branco tem de virar ausência, nunca `0`: zero-zero é um ponto real
 * no Golfo da Guiné, e a loja gravada assim ficaria a milhares de quilômetros
 * de qualquer visitante, sumindo de todo filtro de raio sem ninguém entender
 * por quê. Vírgula é aceita porque é assim que se digita número em português.
 */
const parseCoordinate = (value: string): number | undefined => {
  const parsed = Number(value.trim().replace(',', '.'));
  return value.trim() === '' || Number.isNaN(parsed) ? undefined : parsed;
};

const DOC_TYPE_OPTIONS: ComboItem[] = [
  { id: 'CPF', label: 'CPF' },
  { id: 'CNPJ', label: 'CNPJ' },
];

const STATUS_OPTIONS: ComboItem[] = (Object.keys(MERCHANT_STATUS_LABELS) as MerchantStatus[]).map((s) => ({
  id: s,
  label: MERCHANT_STATUS_LABELS[s],
}));

const SubHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;

  .sub {
    font-size: 0.85rem;
    color: ${(p) => p.theme.textMuted};
  }
`;

const Steps = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
`;

const Step = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: ${(p) => (p.$active ? p.theme.primary : p.theme.textMuted)};

  .dot {
    width: 22px;
    height: 6px;
    border-radius: 999px;
    background: ${(p) => (p.$active ? p.theme.primary : p.theme.border)};
  }
`;

const SectionTitle = styled.h4`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 0.75rem;
  font-size: 0.95rem;
  color: ${(p) => p.theme.textPrimary};
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

const Col = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

const AddressCard = styled.div`
  background: ${(p) => p.theme.surfaceAlt};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radiusLg};
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

const Grid2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.85rem;
`;

const Grid3 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 0.85rem;
`;

const DocGrid = styled.div`
  display: grid;
  grid-template-columns: 130px 1fr;
  gap: 0.85rem;
`;

/**
 * As tres molduras de imagem numa linha so, em modo compacto.
 *
 * Antes eram duas linhas: logomarca + capa numa grade, e o banner sozinho
 * embaixo em largura cheia. Como o `ImageUpload` normal desenha a moldura na
 * largura DISPONIVEL, a capa 16:9 saia com 756x425 e o banner 3:1 com
 * 1000x333 -- medido num Chromium com a geometria real deste modal. O passo
 * dava 944px de conteudo e rolava em TODAS as resolucoes testadas
 * (1280x720, 1366x768, 1440x900 e ate 1920x1080, que estourava por 99px).
 *
 * Em `compact` a altura das molduras e fixa e a largura sai da proporcao:
 * 88x88, 141x88 e 264x88. Cabem lado a lado, o passo cai para 221px e nao
 * rola em nenhuma das quatro resolucoes. De brinde, alinhadas pela mesma
 * altura as tres larguras mostram as proporcoes de uma olhada -- que e o que
 * evita o operador subir a mesma arte nos tres campos.
 *
 * Abaixo de 900px as colunas viram duas: a 1fr de tres colunas nao comporta a
 * moldura de 264px do banner mais o texto de ajuda.
 *
 * O passo ja foi ao tamanho cheio e voltou: o 8cffd62 tirou o `compact` a
 * pedido, e o card #300 trouxe de volta -- em largura de coluna as tres
 * molduras ficam com alturas diferentes, as legendas param em linhas
 * diferentes e o botao de salvar sai da dobra. Se voltar a mexer aqui, o
 * ponto e esse, nao o tamanho em si.
 */
const BrandGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.25rem;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const LocalityChip = styled.button<{ $selected: boolean }>`
  border: 1px solid ${(p) => (p.$selected ? p.theme.primary : p.theme.border)};
  background: ${(p) => (p.$selected ? p.theme.surfaceAlt : 'transparent')};
  color: ${(p) => (p.$selected ? p.theme.primary : p.theme.textMuted)};
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${(p) => p.theme.primary};
    color: ${(p) => p.theme.primary};
  }
`;

/**
 * O que ficou pendente, no fecho do wizard.
 *
 * É a contrapartida de ter soltado a obrigatoriedade (card 241): antes a tela
 * dizia "não deixo salvar"; agora ela salva e diz o que falta. Sem isto o
 * operador só descobriria a lista ao tentar aprovar a empresa, muito depois, e
 * sem lembrar de qual cadastro era.
 */
const Pending = styled.div`
  display: flex;
  gap: 0.7rem;
  padding: 0.85rem 1rem;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surfaceAlt};
  font-size: 0.85rem;
  line-height: 1.5;

  ul {
    margin: 0.35rem 0 0;
    padding-left: 1.1rem;
  }
`;

const Muted = styled.p`
  margin: 0.5rem 0 0;
  font-size: 0.8rem;
  color: ${(p) => p.theme.textMuted};
`;

const Single = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

type DocumentType = 'CPF' | 'CNPJ';

interface WizardForm {
  tradeName: string;
  name: string;
  documentType: DocumentType;
  document: string;
  categoryId: string;
  description: string;
  logoUrl: string;
  coverUrl: string;
  bannerUrl: string;
  zipCode: string;
  street: string;
  number: string;
  complement: string;
  district: string;
  city: string;
  state: string;
  /**
   * Coordenada da loja, como texto (card 150).
   *
   * Texto e não número porque campo de formulário não tem "vazio numérico": o
   * operador apagando o campo produziria `NaN`, que gravado é pior do que a
   * ausência. Vira número - ou `undefined` - só na montagem do payload.
   */
  latitude: string;
  longitude: string;
  email: string;
  phone: string;
  whatsapp: string;
  website: string;
  instagram: string;
  status: MerchantStatus;
  planId: string;
  localityIds: string[];
  hours: WeekHours;
  externalLinks: ExternalLinkRequestDto[];
  /**
   * Equipe inicial. Só existe na criação - ver a KDoc do wizard.
   * A posição 0 é o responsável pela loja e é obrigatória.
   */
  team: MerchantEmployeeFormValue[];
}

const emptyForm = (): WizardForm => ({
  tradeName: '',
  name: '',
  documentType: 'CNPJ',
  document: '',
  categoryId: '',
  description: '',
  logoUrl: '',
  coverUrl: '',
  bannerUrl: '',
  zipCode: '',
  street: '',
  number: '',
  complement: '',
  district: '',
  city: '',
  state: '',
  latitude: '',
  longitude: '',
  email: '',
  phone: '',
  whatsapp: '',
  website: '',
  instagram: '',
  status: 'PENDING',
  planId: '',
  localityIds: [],
  hours: defaultWeekHours(),
  externalLinks: [],
  // Já abre com a linha do responsável: ela é obrigatória, e uma lista vazia
  // faria o operador clicar em "Adicionar funcionário" para descobrir isso.
  team: [emptyMerchantEmployee('OWNER')],
});

const fromMerchant = (m: MerchantDto): WizardForm => {
  const hours = weekHoursFrom(m.businessHours);
  return {
    tradeName: m.tradeName ?? '',
    name: m.name ?? '',
    documentType: (m.document ?? '').replace(/\D/g, '').length > 11 ? 'CNPJ' : 'CPF',
    document: m.document ?? '',
    categoryId: m.categoryId ?? '',
    description: m.description ?? '',
    logoUrl: m.logoUrl ?? '',
    coverUrl: m.coverUrl ?? '',
    bannerUrl: m.bannerUrl ?? '',
    zipCode: m.address?.zipCode ?? '',
    street: m.address?.street ?? '',
    number: m.address?.number ?? '',
    complement: m.address?.complement ?? '',
    district: m.address?.district ?? '',
    city: m.address?.city ?? '',
    state: m.address?.state ?? '',
    latitude: m.address?.latitude != null ? String(m.address.latitude) : '',
    longitude: m.address?.longitude != null ? String(m.address.longitude) : '',
    email: m.email ?? '',
    phone: m.phone ?? '',
    whatsapp: m.whatsapp ?? '',
    website: m.website ?? '',
    instagram: m.instagram ?? '',
    status: m.status,
    planId: m.planId ?? '',
    localityIds: (m.localities ?? []).map((l) => l.id),
    hours,
    externalLinks: externalLinksToRequest(m.externalLinks),
    // Na edição a equipe não vem para o wizard: quem a gerencia é a ficha do
    // lojista, pelos endpoints dedicados (ver a KDoc do wizard).
    team: [],
  };
};

interface MerchantFormModalProps {
  isOpen: boolean;
  merchant: MerchantDto | null;
  categories: ComboItem[];
  categoriesLoading?: boolean;
  /** Active localities (cities) available for selection. */
  localities: ComboItem[];
  localitiesLoading?: boolean;
  /** External sales channel catalog served by the core. */
  externalLinkPlatforms: ExternalLinkPlatformOptionDto[];
  externalLinkPlatformsLoading?: boolean;
  saving: boolean;
  onClose: () => void;
  onSubmit: (payload: MerchantRequestDto) => void;
}

/**
 * Identificador de passo.
 *
 * Era `1 | 2 | 3 | 4 | 5 | 6`, e o número não queria dizer nada: "Marca" entrou
 * no meio da lista com o id 4 e "Equipe" com o 6, então a ordem na tela já não
 * era a ordem dos ids. Com um passo saindo (Responsável), qualquer renumeração
 * silenciosamente reapontaria um `step === N` para o bloco errado - o tipo
 * aceita todos os números iguais. Com nomes, apontar para o passo errado é erro
 * de compilação, e o `setStep('team')` de um `showNotification` se lê sozinho.
 */
type StepId = 'company' | 'brand' | 'channels' | 'team' | 'hours';

/**
 * Merchant form wizard: Empresa → (Marca) → Canais → (Equipe) → Horários.
 *
 * Marca and Canais sit right after Empresa because they are the same subject -
 * how the store shows up to the customer - while Equipe e Horários are about
 * people and operation.
 *
 * NÃO existe mais um passo "Responsável". Ele pedia nome, e-mail e telefone da
 * mesma pessoa que o passo Equipe pede de novo - dois cadastros da mesma
 * pessoa, que divergem no primeiro erro de digitação. Hoje o responsável é a
 * primeira linha da Equipe, obrigatória, e o `responsible` que o core espera é
 * derivado dela (ver `buildResponsible`).
 *
 * The brand step only exists on edit because the image upload endpoint is scoped
 * by merchant id and there is no id before the store exists. Showing it during
 * creation would mean a whole step the operator can only click past - the reason
 * the two upload fields did not belong on the crowded company step either.
 *
 * Equipe é o espelho disso: só existe na CRIAÇÃO. O core lê `employees` apenas
 * no POST e ignora o campo no PUT de propósito - "substituir a equipe" apagaria
 * gente que já tem login. Um passo de equipe na edição ou não faria nada (o
 * campo é descartado) ou teria de gravar na hora, pelos endpoints dedicados -
 * e aí o "Cancelar" do wizard passaria a mentir, porque o que já foi criado
 * não volta atrás. Na edição, a equipe se gerencia na aba Equipe da ficha do
 * lojista, um funcionário por vez, com cada ação salvando sozinha.
 *
 * Consequência disso para a EDIÇÃO (card #285): aqui NÃO há campos do
 * responsável em passo nenhum - nem no EMPRESA, onde eles moraram por um
 * tempo. Aquele arranjo existia porque, sem passo Equipe na edição, não haveria
 * onde corrigir o responsável de um lojista já existente. O motivo caiu: a
 * Equipe virou a FONTE do dado. O core recomputa `merchant.responsible*` a
 * partir do funcionário DONO ativo a cada escrita em funcionário, nos dois
 * editores (`MerchantResponsibleSyncService`), então corrigir o CPF do
 * proprietário na aba Equipe da ficha é o que muda o pagador da cobrança, o
 * contato do lead e o bloco RESPONSIBLE do checklist de aprovação.
 *
 * Manter o card aqui seria manter dois cadastros da mesma pessoa - e o que eles
 * produziam era o defeito mudo que o card #285 corrigiu: os dois divergiam no
 * primeiro erro de digitação e a cobrança seguia com o CPF antigo. Por isso a
 * edição não manda mais `responsible` no payload: ausente é "não mexi", e mexer
 * daqui sobrescreveria o que a Equipe acabou de gravar.
 */
const MerchantFormModal: React.FC<MerchantFormModalProps> = ({
  isOpen,
  merchant,
  categories,
  categoriesLoading,
  localities,
  localitiesLoading,
  externalLinkPlatforms,
  externalLinkPlatformsLoading,
  saving,
  onClose,
  onSubmit,
}) => {
  const { showNotification } = useNotification();
  const isEditing = !!merchant;
  const [step, setStep] = useState<StepId>('company');
  const [form, setForm] = useState<WizardForm>(emptyForm());
  const [geocoding, setGeocoding] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm(merchant ? fromMerchant(merchant) : emptyForm());
      setStep('company');
    }
  }, [isOpen, merchant]);

  const set = <K extends keyof WizardForm>(key: K, value: WizardForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const toggleLocality = (id: string) =>
    setForm((prev) => ({
      ...prev,
      localityIds: prev.localityIds.includes(id)
        ? prev.localityIds.filter((l) => l !== id)
        : [...prev.localityIds, id],
    }));

  const setHour = (day: DayOfWeek, patch: Partial<DayHours>) =>
    setForm((prev) => ({ ...prev, hours: { ...prev.hours, [day]: { ...prev.hours[day], ...patch } } }));

  /**
   * Resolve a coordenada pelo CEP e joga o resultado nos campos da tela.
   *
   * Não salva: o operador vê o ponto, confere, e salva o cadastro como faria
   * com qualquer outra correção. Geocodificar e gravar no mesmo clique tiraria
   * dele a chance de recusar um ponto que caiu no lugar errado.
   *
   * "Não encontrado" é aviso, não erro: CEP sem coordenada conhecida é caso
   * comum fora das capitais, e a loja segue cadastrada sem ponto.
   */
  const geocodeNow = async (): Promise<void> => {
    if (!merchant) return;
    setGeocoding(true);
    try {
      const resolved = await backofficeApi.geocodeMerchant(merchant.id);
      if (!resolved) {
        showNotification('Não foi possível resolver a coordenada por este CEP.', 'error');
        return;
      }
      const atualizado = await backofficeApi.getMerchant(merchant.id);
      setForm((prev) => ({
        ...prev,
        latitude: atualizado.address?.latitude != null ? String(atualizado.address.latitude) : '',
        longitude: atualizado.address?.longitude != null ? String(atualizado.address.longitude) : '',
      }));
      showNotification('Coordenada preenchida. Confira e salve.', 'success');
    } catch {
      showNotification('Não foi possível geocodificar agora.', 'error');
    } finally {
      setGeocoding(false);
    }
  };

  const handleCep = async (raw: string): Promise<void> => {
    const cep = onlyCepDigits(raw);
    set('zipCode', cep);
    if (cep.length !== 8) return;
    setCepLoading(true);
    const found = await lookupCep(cep);
    setCepLoading(false);
    if (!found) return;
    // Só sobrescreve o que o provedor soube responder: quem já digitou o bairro
    // à mão não o perde porque o CEP genérico da cidade não traz bairro.
    setForm((prev) => ({
      ...prev,
      street: found.street || prev.street,
      district: found.district || prev.district,
      city: found.city || prev.city,
      state: found.state || prev.state,
    }));
  };

  /*
    Documento, contato e responsável deixaram de travar o salvamento (card 241).

    Quem cadastra aqui está montando o guia da cidade a partir de informação
    pública: tem nome, categoria e endereço, e quase nunca o CNPJ ou o telefone.
    A obrigatoriedade não produzia o dado - produzia `000.000.000-00` digitado
    para destravar o botão, que é pior do que o campo vazio porque depois
    ninguém distingue de um documento real.

    O que a régua perdeu aqui ela mantém no fim: a empresa nasce PENDING e o
    `approve()` continua cobrando o checklist inteiro. Trocamos "não deixo
    salvar" por "salvo e mostro o que falta" - ver `pendingBlocks` abaixo.

    Opcional não é "sem validação": preenchido pela metade continua sendo erro.
  */
  const documentDigits = form.document.replace(/\D/g, '');
  const documentFormatValid =
    !documentDigits || documentDigits.length === (form.documentType === 'CNPJ' ? 14 : 11);
  const emailFormatValid = !form.email.trim() || EMAIL_PATTERN.test(form.email.trim());
  const phoneDigits = form.phone.replace(/\D/g, '');
  const phoneFormatValid = !phoneDigits || (phoneDigits.length >= 10 && phoneDigits.length <= 11);

  const isCompanyStepValid =
    !!form.tradeName.trim() &&
    !!form.name.trim() &&
    !!form.categoryId &&
    !!form.zipCode &&
    !!form.street &&
    !!form.number &&
    !!form.district &&
    !!form.city &&
    !!form.state &&
    form.localityIds.length > 0 &&
    documentFormatValid &&
    emailFormatValid &&
    phoneFormatValid;

  // The step list is data, not a chain of ternaries: it changes length with
  // `isEditing`, and every place that renders the wizard (footer, subtitle,
  // dots, navigation) has to agree on the order and on where the end is.
  //
  // The ids are stable and independent of position - each one owns a block of
  // fields further down. Reordering the wizard is reordering this array; the
  // blocks stay put.
  const steps: Array<{ id: StepId; label: string; hint: string }> = [
    {
      id: 'company',
      label: 'EMPRESA',
      hint: 'Configure os dados, contato e a localização da empresa.',
    },
    ...(isEditing
      ? ([{ id: 'brand', label: 'MARCA', hint: 'Envie a logomarca e o banner que aparecem no app e no site.' }] as const)
      : []),
    { id: 'channels', label: 'CANAIS', hint: 'Cadastre os links das lojas do lojista em marketplaces, delivery e redes sociais.' },
    ...(isEditing
      ? []
      : ([
          {
            id: 'team',
            label: 'EQUIPE',
            hint: 'Se você já tem o contato do responsável, cadastre agora - a senha temporária vai por e-mail. Pode ficar para depois.',
          },
        ] as const)),
    { id: 'hours', label: 'HORÁRIOS', hint: 'Defina os horários de atendimento.' },
  ];
  // Navegação por posição, e não por aritmética sobre o id: a ordem na tela é a
  // ordem DESTE array (que muda de tamanho com `isEditing`), então "o próximo" é
  // sempre o vizinho aqui - nunca um id calculado.
  const stepIndex = steps.findIndex((it) => it.id === step);
  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === steps.length - 1;
  const externalLinkErrors = validateExternalLinks(form.externalLinks, externalLinkPlatforms);
  const externalLinksValid = !hasExternalLinkErrors(externalLinkErrors);
  // Cobre faixa incompleta, invertida e sobreposta - as mesmas regras do core.
  const businessHoursValid = isWeekHoursComplete(form.hours);
  // Mesmas regras do core (perfil obrigatório para STAFF com acesso, e-mail
  // único), cobradas antes do request. A lista PODE ficar vazia (card 241): sem
  // equipe não há login para provisionar, e é isso mesmo - o acesso do lojista
  // vem depois, quando a operação conseguir o contato ou quando ele reivindicar
  // a loja. Linha começada, porém, continua tendo de fechar: nome, sobrenome e
  // e-mail, que é a chave de login.
  const teamValid = !hasMerchantTeamErrors(validateMerchantTeam(form.team));
  const canAdvance =
    step === 'company'
      ? isCompanyStepValid
      : step === 'hours'
      ? businessHoursValid
      : step === 'channels'
      ? externalLinksValid
      : step === 'team'
      ? teamValid
      : true;

  /**
   * Monta o `responsible` do POST - e só do POST (card #285).
   *
   * Na CRIAÇÃO ele é derivado da primeira linha da equipe - a marcada como
   * "Responsável pela loja": `name` é nome + sobrenome, e e-mail, telefone e
   * CPF vêm dessa mesma linha. O `role` sai do papel da linha, via
   * MERCHANT_ROLE_LABELS: OWNER → "Proprietário".
   *
   * Na EDIÇÃO devolve `undefined`, sempre, porque não há mais formulário de
   * responsável aqui: quem o mantém é a aba Equipe da ficha, e o core
   * recomputa `merchant.responsible*` a partir do funcionário DONO a cada
   * escrita em funcionário. Ausente é exatamente o que dizer "não mexi" - o
   * core faz `request.responsible?.let { ... }` e substitui o objeto INTEIRO,
   * então mandar `{}` daqui apagaria o responsável que a Equipe acabou de
   * gravar.
   */
  const buildResponsible = (): ResponsibleDto | undefined => {
    const filled = (value: ResponsibleDto): ResponsibleDto | undefined =>
      Object.values(value).some(Boolean) ? value : undefined;

    if (isEditing) return undefined;
    const lead = form.team[0];
    if (!lead || isBlankMerchantEmployee(lead)) return undefined;
    return filled({
      name: `${lead.firstName.trim()} ${lead.lastName.trim()}`.trim() || undefined,
      role: MERCHANT_ROLE_LABELS[lead.role],
      email: lead.email.trim() || undefined,
      phone: lead.phone.replace(/\D/g, '') || undefined,
      document: lead.document.replace(/\D/g, '') || undefined,
    });
  };

  /*
    Espelha `Merchant.onboardingChecklist` do core, bloco a bloco e com os mesmos
    critérios. Não é a régua - a régua é do core, no `approve()`. É o aviso de
    que ela existe, mostrado enquanto o cadastro ainda está aberto.

    MARCA e CANAIS ficam de fora de propósito: não entram no checklist de
    aprovação, e listá-los aqui faria o operador correr atrás do que não bloqueia
    nada.
  */
  const responsible = buildResponsible();
  /*
    O bloco do responsável é o único que a EDIÇÃO não consegue avaliar pelo
    formulário: os campos saíram daqui (card #285) e quem o preenche agora é a
    Equipe, pelos endpoints dela. Então na edição a resposta vem do que o core
    já tem gravado - que é o mesmo dado que o `approve()` vai cobrar. Ler o
    `responsible` do formulário aqui faria a edição acusar "falta responsável"
    em toda loja que tem um.
  */
  const responsibleForChecklist = isEditing ? merchant?.responsible : responsible;
  const pendingBlocks: string[] = [
    !form.document.trim() || !form.description.trim() ? 'Dados da empresa (documento e descrição)' : null,
    !form.email.trim() || !form.phone ? 'Contato da empresa (e-mail e telefone)' : null,
    !responsibleForChecklist?.name || !responsibleForChecklist?.email || !responsibleForChecklist?.phone
      ? isEditing
        // Na edição o operador não tem como resolver isto aqui - o passo Equipe
        // não existe e os campos saíram. Dizer só "falta" o mandaria procurar um
        // formulário que não há mais.
        ? 'Responsável pela loja (nome, e-mail e telefone) - cadastre o proprietário na aba Equipe da ficha'
        : 'Responsável pela loja (nome, e-mail e telefone)'
      : null,
    !isWeekHoursComplete(form.hours) ? 'Horários de atendimento' : null,
    !form.planId ? 'Plano contratado' : null,
  ].filter((it): it is string => !!it);

  const submit = (): void => {
    if (!externalLinksValid) {
      showNotification('Revise os canais de venda antes de salvar.', 'error');
      setStep('channels');
      return;
    }
    if (!businessHoursValid) {
      showNotification('Revise os horários de atendimento antes de salvar.', 'error');
      setStep('hours');
      return;
    }
    if (!isEditing && !teamValid) {
      showNotification('Revise a equipe antes de salvar - comece uma linha e ela precisa ser concluída.', 'error');
      setStep('team');
      return;
    }
    const payload: MerchantRequestDto = {
      name: form.name.trim(),
      tradeName: form.tradeName.trim(),
      // Vazio vai como ausente, nunca como string em branco: `''` gravado é um
      // documento que a próxima tela mostra como preenchido e não está.
      document: form.document || undefined,
      categoryId: form.categoryId,
      email: form.email.trim() || undefined,
      phone: form.phone || undefined,
      whatsapp: form.whatsapp.trim() || undefined,
      website: form.website.trim() || undefined,
      instagram: form.instagram.trim() || undefined,
      status: form.status,
      description: form.description.trim() || undefined,
      logoUrl: form.logoUrl || undefined,
      coverUrl: form.coverUrl || undefined,
      bannerUrl: form.bannerUrl || undefined,
      planId: form.planId || undefined,
      localityIds: form.localityIds,
      // Só na criação, e derivado da Equipe. Na edição sai `undefined`, que o
      // core lê como "não mexi" - o responsável de um lojista existente é
      // recomputado a partir do funcionário DONO, não enviado daqui.
      responsible,
      address: {
        zipCode: form.zipCode,
        street: form.street,
        number: form.number,
        complement: form.complement || undefined,
        district: form.district,
        city: form.city,
        state: form.state,
        // Vazio vai como ausente: `0` gravado é o Golfo da Guiné, e a loja
        // sumiria de todo filtro de raio sem ninguém entender por quê.
        latitude: parseCoordinate(form.latitude),
        longitude: parseCoordinate(form.longitude),
      },
      businessHours: weekHoursToPayload(form.hours),
      // Same convention as `localityIds`: the field is always on screen, so the
      // list always goes - omitting it would mean "keep what is there" and the
      // operator's removals would be silently discarded.
      externalLinks: externalLinksToPayload(form.externalLinks),
      // Só na criação. No PUT o core descarta `employees`, então mandar a lista
      // ali seria prometer na tela uma gravação que não acontece.
      ...(isEditing ? {} : { employees: merchantTeamToPayload(form.team) }),
    };
    onSubmit(payload);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="1040px"
      title={isEditing ? 'Editar lojista' : 'Novo lojista'}
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => (isFirstStep ? onClose() : setStep(steps[stepIndex - 1].id))}
          >
            {isFirstStep ? 'Cancelar' : 'Voltar'}
          </Button>
          {isLastStep ? (
            <Button variant="primary" isLoading={saving} onClick={submit}>
              {isEditing ? 'Salvar alterações' : 'Criar lojista'}
            </Button>
          ) : (
            <Button
              variant="primary"
              disabled={!canAdvance}
              onClick={() => setStep(steps[stepIndex + 1].id)}
            >
              Próximo passo
            </Button>
          )}
        </>
      }
    >
      <SubHeader>
        <div className="sub">{steps.find((it) => it.id === step)?.hint}</div>
        <Steps>
          {steps.map((it) => (
            <Step key={it.id} $active={step === it.id}>
              <span className="dot" /> {it.label}
            </Step>
          ))}
        </Steps>
      </SubHeader>

      {/*
        Sem título "Dados da empresa": o passo já se chama EMPRESA no indicador
        e a linha de apoio logo acima já diz o que se configura aqui - o título
        era a terceira vez que a mesma frase aparecia na mesma tela.
      */}
      {step === 'company' && (
        <TwoCol>
          <Col>
            <TextInput label="Nome fantasia" value={form.tradeName} onChange={(e) => set('tradeName', e.target.value)} />
            <TextInput label="Razão social" value={form.name} onChange={(e) => set('name', e.target.value)} />
            <DocGrid>
              <SearchableComboBox
                label="Tipo de documento (opcional)"
                items={DOC_TYPE_OPTIONS}
                valueId={form.documentType}
                onChange={(id) => set('documentType', id as DocumentType)}
              />
              <DocumentInput
                label="Número do documento (opcional)"
                value={form.document}
                onChange={(v) => set('document', v)}
              />
            </DocGrid>
            <Grid2>
              <SearchableComboBox
                label="Categoria"
                placeholder="Selecione a categoria"
                items={categories}
                valueId={form.categoryId || undefined}
                loading={categoriesLoading}
                onChange={(id) => set('categoryId', id)}
              />
              {/*
                Status é do LOJISTA, não de uma pessoa. Estava no passo do
                responsável, que é o último lugar onde alguém procuraria pela
                situação da loja; aqui fica ao lado da categoria, os dois
                atributos que classificam a empresa.
              */}
              <SearchableComboBox
                label="Status"
                items={STATUS_OPTIONS}
                valueId={form.status}
                onChange={(id) => set('status', id as MerchantStatus)}
              />
            </Grid2>
            <div>
              <SectionTitle>
                <Map size={16} /> Localidades atendidas
              </SectionTitle>
              <ChipRow>
                {localitiesLoading && <span style={{ fontSize: '0.82rem' }}>Carregando cidades...</span>}
                {!localitiesLoading && localities.length === 0 && (
                  <span style={{ fontSize: '0.82rem' }}>Nenhuma cidade cadastrada - cadastre em Localidades.</span>
                )}
                {localities.map((locality) => (
                  <LocalityChip
                    key={locality.id}
                    type="button"
                    $selected={form.localityIds.includes(locality.id)}
                    onClick={() => toggleLocality(locality.id)}
                  >
                    {locality.label}
                  </LocalityChip>
                ))}
              </ChipRow>
            </div>
            <RichTextInput
              label="Descrição"
              placeholder="Descreva a empresa, diferenciais, etc."
              value={form.description}
              onChange={(html) => set('description', html)}
            />

          </Col>
          <Col>
            <AddressCard>
              <SectionTitle>
                <MapPin size={16} /> Endereço
              </SectionTitle>
              <Grid2>
                <TextInput
                  label={cepLoading ? 'CEP (buscando...)' : 'CEP'}
                  value={maskCep(form.zipCode)}
                  onChange={(e) => handleCep(e.target.value)}
                />
                <TextInput label="Rua" value={form.street} onChange={(e) => set('street', e.target.value)} />
              </Grid2>
              <Grid2>
                <TextInput label="Número" value={form.number} onChange={(e) => set('number', e.target.value)} />
                <TextInput
                  label="Complemento"
                  placeholder="Ex: Sala 101"
                  value={form.complement}
                  onChange={(e) => set('complement', e.target.value)}
                />
              </Grid2>
              <Grid3>
                <TextInput label="Bairro" value={form.district} onChange={(e) => set('district', e.target.value)} />
                <TextInput label="Cidade" value={form.city} onChange={(e) => set('city', e.target.value)} />
                <SearchableComboBox
                  label="Estado (UF)"
                  items={UF_OPTIONS}
                  valueId={form.state || undefined}
                  onChange={(id) => set('state', id)}
                />
              </Grid3>
              {/*
                Card 150. A coordenada existe na tela porque ela é o que faz
                "mais próximas" medir alguma coisa - e porque, quando o CEP não
                resolve, alguém precisa conseguir apontar o lugar à mão.
                Digitado vence geocodificado: quem abriu o mapa sabe mais do que
                o CEP sabe.
              */}
              <Grid2>
                <TextInput
                  label="Latitude (opcional)"
                  placeholder="-24.7135"
                  value={form.latitude}
                  onChange={(e) => set('latitude', e.target.value)}
                />
                <TextInput
                  label="Longitude (opcional)"
                  placeholder="-53.7431"
                  value={form.longitude}
                  onChange={(e) => set('longitude', e.target.value)}
                />
              </Grid2>
              {isEditing && merchant && (
                <div>
                  <Button
                    type="button"
                    variant="outline"
                    isLoading={geocoding}
                    onClick={geocodeNow}
                  >
                    <MapPin size={15} /> Geocodificar agora
                  </Button>
                  {/* O endereço usado fica à vista: sem isso, "não foi possível"
                      não diz se o problema é o CEP errado ou o provedor. */}
                  <Muted>
                    A partir do CEP {maskCep(form.zipCode) || '-'}
                    {form.city ? `, ${form.city}` : ''}
                    {form.state ? `/${form.state}` : ''}
                  </Muted>
                </div>
              )}
            </AddressCard>
            <AddressCard>
              <SectionTitle>
                <Phone size={16} /> Contato
              </SectionTitle>
              <Grid2>
                <TextInput
                  label="E-mail (opcional)"
                  type="email"
                  value={form.email}
                  onChange={(e) => set('email', e.target.value)}
                />
                <PhoneInput label="Telefone (opcional)" value={form.phone} onChange={(v) => set('phone', v)} />
              </Grid2>
              <Grid2>
                <PhoneInput label="WhatsApp" value={form.whatsapp} onChange={(v) => set('whatsapp', v)} />
                <TextInput label="Instagram" placeholder="@loja" value={form.instagram} onChange={(e) => set('instagram', e.target.value)} />
              </Grid2>
              <TextInput label="Site" placeholder="https://" value={form.website} onChange={(e) => set('website', e.target.value)} />
            </AddressCard>
          </Col>
        </TwoCol>
      )}

      {step === 'hours' && (
        <Single>
          <div>
            <SectionTitle>
              <Clock size={16} /> Horários de atendimento
            </SectionTitle>
            <BusinessHoursInput value={form.hours} onChange={setHour} />
          </div>
          {/*
            No último passo, e não numa validação que trava: o cadastro pode ser
            salvo assim. O que a lista diz é que a empresa vai nascer PENDING e
            que estes blocos serão cobrados na aprovação - a régua continua
            inteira, só deixou de ser cobrada na porta de entrada.
          */}
          {pendingBlocks.length > 0 && (
            <Pending>
              <AlertTriangle size={18} />
              <div>
                Dá para salvar assim: a empresa entra como <strong>pendente</strong> e aparece no
                guia. Para aprovar depois, vai faltar:
                <ul>
                  {pendingBlocks.map((it) => (
                    <li key={it}>{it}</li>
                  ))}
                </ul>
              </div>
            </Pending>
          )}
        </Single>
      )}

      {step === 'channels' && (
        <Single>
          <div>
            <SectionTitle>
              <Link2 size={16} /> Canais de venda
            </SectionTitle>
            <ExternalLinksInput
              value={form.externalLinks}
              onChange={(links) => set('externalLinks', links)}
              platforms={externalLinkPlatforms}
              platformsLoading={externalLinkPlatformsLoading}
            />
          </div>
        </Single>
      )}

      {step === 'team' && (
        <Single>
          <div>
            <SectionTitle>
              <Users size={16} /> Equipe da loja
            </SectionTitle>
            <MerchantTeamInput
              value={form.team}
              onChange={(team) => set('team', team)}
              /*
                Lista vazia por construção, não por falta de carregamento: o
                endpoint de perfis é escopado por lojista e a loja ainda não
                existe - não há id para consultar. Logo, no cadastro só cabe o
                proprietário, e o componente desabilita "Funcionário" com o
                aviso abaixo em vez de deixar o operador bater em
                ACCESS_PROFILE_REQUIRED.
              */
              profiles={[]}
              noProfilesHint={
                <>
                  A primeira pessoa da lista é o <strong>responsável pela loja</strong> - é dela
                  que saem os dados do responsável do cadastro. A lista pode ficar{' '}
                  <strong>vazia</strong>: sem contato do lojista o acesso é provisionado depois, na
                  aba Equipe da ficha. Perfis de acesso são criados pelo próprio lojista, no painel
                  dele, e uma loja que está nascendo não tem nenhum; por isso aqui só dá para
                  cadastrar <strong>proprietários</strong>, que são super-usuários da loja e não
                  dependem de perfil.
                </>
              }
              /*
                A marcação do responsável é POSICIONAL: a primeira linha. Um
                rádio "este é o responsável" deixaria o operador salvar sem
                escolher ninguém (ou escolher dois); a posição sempre tem
                resposta. E como sem perfil de acesso toda linha é OWNER, "o
                primeiro OWNER" e "a primeira linha" são a mesma coisa - só que
                a segunda é visível na tela.
              */
              leadLabel="Responsável pela loja"
            />
          </div>
        </Single>
      )}

      {/*
        Brand images live in their own step: they are the only fields that
        upload on change instead of on save, and the picture drop zones wedged
        between text inputs made the company step read as a gallery. This step
        is only reachable while editing (see the wizard KDoc).
      */}
      {step === 'brand' && (
        <Single>
          <div>
            <SectionTitle>
              <Image size={16} /> Marca
            </SectionTitle>
            <BrandGrid>
              <ImageUpload
                label="Logomarca"
                variant="logo"
                compact
                value={form.logoUrl || undefined}
                onChange={(url) => set('logoUrl', url ?? '')}
                onUpload={uploadMerchantImage(merchant?.id ?? '', 'LOGO')}
                helpText="Aparece nas listagens e no topo da página. Formato 1:1."
                onError={(message) => showNotification(message, 'error')}
              />
              {/*
                Capa e banner são slots diferentes desde esta leva: a capa é a
                miniatura do cartão (lida a ~200px numa grade) e o banner é o fundo
                da página da loja. O texto de ajuda diz onde cada uma aparece
                porque, sem isso, o operador sobe a mesma arte nos dois campos.
              */}
              {/*
                `merchantCover`, e não `cover` (card #302): a moldura desenhava
                16:9 enquanto este texto pedia 16:10, e quem seguia o texto via
                o preview cortar as bordas da arte. Quem tinha razão era o
                texto - a capa da loja é lida 16:10 no cartão da vitrine e da
                busca do site (`.business-card__media`), e é lá que ela
                aparece. Corrigido também o número entre parênteses, que dizia
                1280×720 (16:9) ao lado da frase que pede 16:10.
              */}
              <ImageUpload
                label="Capa"
                variant="merchantCover"
                compact
                value={form.coverUrl || undefined}
                onChange={(url) => set('coverUrl', url ?? '')}
                onUpload={uploadMerchantImage(merchant?.id ?? '', 'COVER')}
                helpText="Miniatura nos cartões da vitrine e da busca. Formato 16:10 (1280×800)."
                onError={(message) => showNotification(message, 'error')}
              />
              <ImageUpload
                label="Banner"
                variant="banner"
                compact
                value={form.bannerUrl || undefined}
                onChange={(url) => set('bannerUrl', url ?? '')}
                onUpload={uploadMerchantImage(merchant?.id ?? '', 'BANNER')}
                helpText="Fundo da página da loja, atrás do nome. Imagem larga, 3:1 (1920×640)."
                onError={(message) => showNotification(message, 'error')}
              />
            </BrandGrid>
          </div>
        </Single>
      )}
    </Modal>
  );
};

export default MerchantFormModal;
